import { useMemo } from 'react'
import { DateTime } from 'luxon'

import { Alert, Card, CardColors, Popup, Text } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsCatchUpBookkeepingComplete } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import {
  selectLastYearIncome,
  selectProjectedIncome,
  selectYtdIncome,
} from '@/features/goal-setting/goals.selectors'
import { formatCurrencyWithDefault } from '@/features/goal-setting/helpers'
import { TypographyStyles } from '@/styles'

export const IncomeTab = () => {
  const catchUpBookkeepingComplete = useReselector(
    selectIsCatchUpBookkeepingComplete
  )

  const currentYear = useMemo(() => DateTime.now().year, [])
  const ytdIncome = useReselector(selectYtdIncome)
  const projectedIncome = useReselector(selectProjectedIncome)
  const lastYearIncome = useReselector(selectLastYearIncome)

  return (
    <>
      <Text variant={TypographyStyles.H1} className="mt-10">
        Income Goal Help
      </Text>

      <Text variant={TypographyStyles.BODY_MD} className="mt-2">
        Income is the total amount your business brings into your practice.
        <br />
        <br />
        Based upon your current performance, we’ve projected your income for the
        year to help you set your income goal.
      </Text>

      <Card color={CardColors.LEVEL_1} className="mt-8">
        <Text variant={TypographyStyles.LABEL_MD}>
          Your Practice Income
          <Popup
            content={
              <>
                <Text variant={TypographyStyles.H3}>Projected Income</Text>
                <Text variant={TypographyStyles.BODY_SM} className="mt-3">
                  This is calculated by taking how much you&apos;ve made this
                  year, and dividing it by how many days have already passed.
                  Then, to project out for the year, you multiply this by the
                  number of days in a year (365).
                  <br />
                  <br />
                  For example, as of May 10, you would be 130 days into the
                  year. If your income totals $52,000 so far, divide $65,000/130
                  days. This calculates to a rate of $400/day.
                  <br />
                  <br />
                  To project out for the rest of the year, multiply that rate of
                  $400/day by the number of days per year. This would give you
                  projected income of $146,000.
                </Text>
              </>
            }
          />
        </Text>
        <div className="flex mt-6">
          <div className="w-1/2">
            <Text variant={TypographyStyles.LABEL_SM}>
              Projected {currentYear} Income
            </Text>
            <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
              {formatCurrencyWithDefault(
                projectedIncome,
                catchUpBookkeepingComplete
              )}
            </Text>
            <Text variant={TypographyStyles.BODY_SM} className="mt-2">
              Based on YTD Income of{' '}
              {formatCurrencyWithDefault(ytdIncome, catchUpBookkeepingComplete)}
            </Text>
          </div>
          {catchUpBookkeepingComplete && (
            <div className="w-1/2">
              <Text variant={TypographyStyles.LABEL_SM}>
                {currentYear - 1} Income
              </Text>
              <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
                {formatCurrencyWithDefault(
                  lastYearIncome,
                  catchUpBookkeepingComplete
                )}
              </Text>
            </div>
          )}
        </div>
        {!catchUpBookkeepingComplete && (
          <Alert variant="warning" className="mt-6">
            We’ll be able to provide you with a projected profit once your
            catch-up bookkeeping is complete.
          </Alert>
        )}
      </Card>
    </>
  )
}
